import React, { lazy, Suspense, useMemo } from 'react';
import { Navigate, Route, RouteProps, Routes } from 'react-router-dom';

import { useFlag } from '@unleash/proxy-client-react';

import { useAccess } from '../hooks/useAccess';
import { useApp } from '../hooks/useApp';
import { isAdminOrSales, isDigitalProductAdminUser } from '../services/UserRole';
import { LandingPage } from './LandingPage/LandingPage';
import { LandingPageV2 } from './LandingPageV2/LandingPageV2';
import { LoadingSpinnerPage } from './LoadingSpinnerPage';

const getPageLazily = (filename: string, name: string) =>
  lazy(() => import(`${filename}/${name}`).then((module) => ({ default: module[name] })));

const getProjectPageLazily = (name: string) =>
  React.lazy(() =>
    import(`../projects/pages/${name}/${name}`).then((module) => ({ default: module[name] })),
  );

const SelectWorkstationPage = getPageLazily('./SelectWorkstationPage', 'SelectWorkstationPage');
const AssemblyPage = getPageLazily('./AssemblyPage', 'AssemblyPage');
const AuthRedirect = getPageLazily('./AuthRedirect', 'AuthRedirect');
const ImprintPage = getPageLazily('./ImprintPage', 'ImprintPage');
const ConfiguratorPage = getPageLazily('./ConfiguratorPage', 'ConfiguratorPageWithProviders');
const ExportsPage = getPageLazily('./ExportsPage', 'ExportsPage');
const AdminFilesPage = getPageLazily('./AdminFilesPage', 'AdminFilesPage');
const AdminModelsPage = getPageLazily('./AdminModelsPage', 'AdminModelsPage');
const AdminProductsPage = getPageLazily('./AdminProductsPage', 'AdminProductsPage');
const AdminArticlesPage = getPageLazily('./AdminArticlesPage', 'AdminArticlesPage');
const AdminArticleDetailsPage = getPageLazily(
  './AdminArticleDetailsPage',
  'AdminArticleDetailsPage',
);
const AdminProductDetailsPage = getPageLazily(
  './AdminProductDetailsPage',
  'AdminProductDetailsPage',
);
const ProjectsPage = getProjectPageLazily('ProjectsPage');
const SalesProjectsPage = getProjectPageLazily('SalesProjectsPage');
const DraftsPage = getProjectPageLazily('DraftsPage');
const DashboardPage = getProjectPageLazily('DashboardPage');
const AdminModelEditorPage = getPageLazily('./AdminModelEditorPage', 'AdminModelEditorPage');

type RouteData = Required<Pick<RouteProps, 'path' | 'element'>>;
export const CustomRoutes: React.FC = () => {
  const { loggedIn } = useApp();
  const { isPermittedToAccessAdminPage } = useAccess();
  const { roles } = useApp();
  const isDigitalProductAdmin = isDigitalProductAdminUser(roles);
  const isUserAdminOrSales = isAdminOrSales(roles);
  const internalFeaturesFlag = useFlag('wsc.internal-features');
  const landingPageV2Flag = useFlag('wsc.home-page-v2');

  const routes = useMemo(() => {
    const list: RouteData[] = [
      { path: '/', element: landingPageV2Flag ? <LandingPageV2 /> : <LandingPage /> },
      { path: '/demo', element: landingPageV2Flag ? <LandingPageV2 /> : <LandingPage /> },
      { path: '/select-workstation', element: <SelectWorkstationPage /> },
      { path: '/assemble/:templateOrNumber', element: <AssemblyPage /> },
      { path: '/configurator/:configurationId', element: <ConfiguratorPage /> },
      { path: '/authenticate-redirect', element: <AuthRedirect /> },
      { path: '/end-session-redirect', element: <AuthRedirect /> },
      { path: '/imprint', element: <ImprintPage /> },
    ];
    if (loggedIn) {
      list.push(
        ...[
          { path: '/projects', element: <ProjectsPage /> },
          { path: '/sales-projects', element: <SalesProjectsPage /> },
          { path: '/drafts', element: <DraftsPage /> },
          { path: '/admin/exports', element: <ExportsPage /> },
        ],
      );
      if (isUserAdminOrSales) {
        list.push(...[{ path: '/projects/dashboard', element: <DashboardPage /> }]);
      }
      if (isDigitalProductAdmin) {
        list.push(...[{ path: '/projects/tests-projects', element: <SalesProjectsPage /> }]);
      }
      if (internalFeaturesFlag) {
        list.push(
          ...[
            { path: '/admin/products', element: <AdminProductsPage /> },
            { path: '/admin/products/:id', element: <AdminProductDetailsPage /> },
            { path: '/admin/articles', element: <AdminArticlesPage /> },
            { path: '/admin/articles/:id', element: <AdminArticleDetailsPage /> },
            { path: '/admin/files', element: <AdminFilesPage /> },
            { path: '/admin/models', element: <AdminModelsPage /> },
            { path: '/admin/models/:modelId', element: <AdminModelEditorPage /> },
            { path: '/admin/models/:modelId/edit', element: <AdminModelEditorPage /> },
          ],
        );
      }
    }
    if (isPermittedToAccessAdminPage) {
      list.push({ path: '/sales-projects', element: <SalesProjectsPage /> });
    }
    return list;
  }, [
    loggedIn,
    isPermittedToAccessAdminPage,
    internalFeaturesFlag,
    landingPageV2Flag,
    isDigitalProductAdmin,
    isUserAdminOrSales,
  ]);

  return (
    <Suspense fallback={<LoadingSpinnerPage />}>
      <Routes>
        {routes.map((props, i) => (
          <Route key={i} {...props} />
        ))}
        <Route path="*" element={<Navigate replace to="/" />} />
      </Routes>
    </Suspense>
  );
};
